import { selMessage } from "@selector/shared";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { type ISelectOption } from "@/app-utils/form.ts";
import {
  type BlastIndex,
  type BlastJob,
  type BlastViewState,
  type Contig,
  type InputJobInput,
  type InputJobValidation,
  type InputSequenceIdentifiersInput,
  type Range,
  type ValidationError,
  type View,
} from "@/graphql.ts";
import { BLAST_METHOD, ENTITY_TYPE, JOB_TYPES, MONOMER_TYPE, STRAND_TYPE, VIEW } from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { buildBlastSubmitMessage } from "../../app-utils/alert.js";
import { notify } from "../../modules/notifications/notify.js";
import {
  CLEAR_SEQ_IDENTIFIERS,
  FILE_GLOBAL_VALIDATION_FAILED,
  FILE_VALIDATION_COMPLETE,
  FILE_VALIDATION_FAILED,
  FILE_VALIDATION_REQUEST,
  FILE_VALIDATION_WAITING,
  LOAD_BLAST_INDEX_LIST_FAILURE,
  LOAD_BLAST_INDEX_LIST_REQUEST,
  LOAD_BLAST_INDEX_LIST_SUCCESS,
  LOAD_SEQ_IDENTIFIERS_FAILURE,
  LOAD_SEQ_IDENTIFIERS_REQUEST,
  LOAD_SEQ_IDENTIFIERS_SUCCESS,
  LOAD_VIEW_SUCCESS,
  SUBMIT_BLAST_JOB_FAILURE,
  SUBMIT_BLAST_JOB_REQUEST,
  SUBMIT_BLAST_JOB_SUCCESS,
  TOGGLE_COLORING_MODE,
} from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { createReducer, type ReduxAction } from "../redux-utils.js";
import { type IFileUpload } from "./file-upload.js";
import { showNavbarElement } from "./navbar.js";
import { refreshEventsListing } from "./playbook-execution.js";

/**
 * State definition
 */
export interface IBlastState {
  colorMismatchesOnly: boolean;
  prefilledValues: IBlastPrefilledValuesState;
}

export interface IBlastPrefilledValuesState {
  blastMethod?: BLAST_METHOD;
  sequenceIdentifiers: string[]; // sequences or regions being send from table or genome browser to BLAST
}

const initialState: IBlastState = {
  colorMismatchesOnly: true,
  prefilledValues: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_SEQ_IDENTIFIERS_REQUEST]: (state: IBlastState) => {
    return {
      ...state,
      prefilledValues: null,
    };
  },

  [LOAD_SEQ_IDENTIFIERS_SUCCESS]: (state: IBlastState, payload: IBlastPrefilledValuesState) => {
    return {
      ...state,
      prefilledValues: payload,
    };
  },

  [LOAD_SEQ_IDENTIFIERS_FAILURE]: (state: IBlastState) => {
    return {
      ...state,
      prefilledValues: null,
    };
  },

  [CLEAR_SEQ_IDENTIFIERS]: (state: IBlastState) => {
    return {
      ...state,
      prefilledValues: null,
    };
  },

  [TOGGLE_COLORING_MODE]: (state: IBlastState): IBlastState => {
    return {
      ...state,
      colorMismatchesOnly: !state.colorMismatchesOnly,
    };
  },

  [LOAD_VIEW_SUCCESS]: (state: IBlastState, payload: View) => {
    if (payload.viewType === VIEW.BLAST_JOB_VIEW.type) {
      // Add the the columns as a special condition to the beginning of the available annotations list
      const blastView: BlastViewState = payload.state.blastView;

      return {
        ...state,
        colorMismatchesOnly: blastView.colorMismatchesOnly,
      };
    } else {
      return state;
    }
  },
});

/**
 * Action creators
 */

export function loadBlastIndexList(searchTerm: string) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<BlastIndex[]> => {
    dispatch({
      type: LOAD_BLAST_INDEX_LIST_REQUEST,
    });

    return webApiClient
      .fetchBlastIndexList(searchTerm)
      .then((response: BlastIndex[]) => {
        dispatch({
          type: LOAD_BLAST_INDEX_LIST_SUCCESS,
          payload: response,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_BLAST_INDEX_LIST_FAILURE,
          alertTitle: "Fetching of Blast Indices Failed",
        }),
      );
  };
}

export function submitBlastJob(jobInput: InputJobInput) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<BlastJob> => {
    dispatch({
      type: SUBMIT_BLAST_JOB_REQUEST,
      payload: jobInput.name,
    });

    return webApiClient
      .submitJob(jobInput)
      .then((job: BlastJob) => {
        dispatch({
          type: SUBMIT_BLAST_JOB_SUCCESS,
          payload: job,
        });

        notify.info({
          title: selMessage({ id: "job.submission.successTitle" }),
          children: buildBlastSubmitMessage(job),
        });

        dispatch(refreshEventsListing());

        return job;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SUBMIT_BLAST_JOB_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function forwardContigRangeToBlast(contig: Contig, range: Range): ReduxAction<Promise<BlastJob>> {
  return (dispatch, _getState, webApiClient) => {
    const seqIdentifiersInput: InputSequenceIdentifiersInput = {
      region: {
        contigId: contig.id,
        range: {
          start: range.start,
          stop: range.stop,
        },
        strand: STRAND_TYPE.PLUS,
      },
    };

    dispatch({ type: LOAD_SEQ_IDENTIFIERS_REQUEST });

    return webApiClient
      .fetchSequenceIdentifiers(seqIdentifiersInput)
      .then((seqIdentifiers: string[]) => {
        const prefilledValues: IBlastPrefilledValuesState = {
          sequenceIdentifiers: seqIdentifiers,
        };

        dispatch({
          type: LOAD_SEQ_IDENTIFIERS_SUCCESS,
          payload: prefilledValues,
        });

        dispatch(showNavbarElement({ showBlastPlaybookWizard: true }));
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_SEQ_IDENTIFIERS_FAILURE,
          actionPayload: seqIdentifiersInput,
          alertTitle: "Loading Sequence Identifiers Failed",
        }),
      );
  };
}

export function mapFeatureTypeToMonomer(featureType: string): string {
  //  mapFeatureTypeToMonomer( entityType: key of ENTITY_TYPE ): key of MONOMER_TYPE
  switch (featureType) {
    case ENTITY_TYPE.GENE:
    case ENTITY_TYPE.GENE_MUTATION:
    case ENTITY_TYPE.TRANSCRIPT:
    case ENTITY_TYPE.TRANSCRIPT_LOCATION:
    case ENTITY_TYPE.VARIANT:
    case ENTITY_TYPE.MUTATION:
    case ENTITY_TYPE.CONTIG:
    case ENTITY_TYPE.NUFEATURE:
    case ENTITY_TYPE.MARKER_NUS:
      return MONOMER_TYPE.NU;
    case ENTITY_TYPE.PROTEIN:
    case ENTITY_TYPE.PROTEIN_LOCATION:
    case ENTITY_TYPE.PROTEIN_MUTATION:
    case ENTITY_TYPE.AAFEATURE:
    case ENTITY_TYPE.ORTHOLOG:
    case ENTITY_TYPE.DIRECTIONAL_ORTHOLOG:
      return MONOMER_TYPE.AA;
  }
}

export function forwardFeaturesToBlast(featureType: string, featureIds: number[]): ReduxAction<Promise<BlastJob>> {
  return (dispatch, _getState, webApiClient) => {
    const seqIdentifiersInput: InputSequenceIdentifiersInput = {
      featureType,
      featureIds,
    };

    dispatch({ type: LOAD_SEQ_IDENTIFIERS_REQUEST });

    return webApiClient
      .fetchSequenceIdentifiers(seqIdentifiersInput)
      .then((seqIdentifiers: string[]) => {
        const monomer: string = mapFeatureTypeToMonomer(featureType);
        const blastMethod: BLAST_METHOD = monomer === MONOMER_TYPE.AA ? BLAST_METHOD.BLASTP : BLAST_METHOD.BLASTN;
        const prefilledValues: IBlastPrefilledValuesState = {
          sequenceIdentifiers: seqIdentifiers,
          blastMethod: blastMethod,
        };
        dispatch({
          type: LOAD_SEQ_IDENTIFIERS_SUCCESS,
          payload: prefilledValues,
        });
        dispatch(showNavbarElement({ showBlastPlaybookWizard: true }));
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_SEQ_IDENTIFIERS_FAILURE,
          actionPayload: seqIdentifiersInput,
          alertTitle: "Loading Sequence Identifiers Failed",
        }),
      );
  };
}

export function clearSequenceIdentifiers() {
  return { type: CLEAR_SEQ_IDENTIFIERS };
}

/**
 * Perform server side validation of the blast sequence or blast uploaded files, and gives back an array of errors {{IValidationError}}.
 * The result is forwarded to the asyncValidate method of the redux-form, because redux-form specific formatting is necessary to feed back the errors.
 */
export function validateBlastInputSequence(
  blastMethod: ISelectOption,
  sequence: string,
  filesToBeValidated: IFileUpload[],
) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<ValidationError[]> => {
    if (filesToBeValidated) {
      dispatch({ type: FILE_VALIDATION_REQUEST, payload: filesToBeValidated });
    }

    const jobValidation: InputJobValidation = {
      type: JOB_TYPES.BLAST,
      blastType: blastMethod.value,
      // make sure that when files are uploaded, an empty query is submitted for validation
      sequenceQuery: sequence,
      files: filesToBeValidated?.map((file: IFileUpload) => file.file?.uploaded),
    };

    return webApiClient.validateBlastInputSequence(jobValidation).then((validationErrors: ValidationError[]) => {
      if (filesToBeValidated) {
        const hasErrors = validationErrors?.length;
        if (hasErrors) {
          // some validationError.inputName can be null: that means we consider all files as failed
          // (eg due to some form-wide validation like the accumulated number of seqs is too large)
          const globalValidationErrors: ValidationError[] = validationErrors.filter(
            (error: ValidationError) => !error.inputName,
          );

          const fileValidationErrors: ValidationError[] = validationErrors.filter(
            (error: ValidationError) => error.inputName,
          );

          // dispatch a flag for files whose validation succeeded or failed
          dispatch({
            type: FILE_VALIDATION_WAITING,
            payload: filesToBeValidated.map((fileUpload) => fileUpload.file),
          });

          if (globalValidationErrors.length > 0) {
            dispatch({ type: FILE_GLOBAL_VALIDATION_FAILED });
          }

          if (fileValidationErrors.length > 0) {
            dispatch({ type: FILE_VALIDATION_FAILED, payload: fileValidationErrors });
          }
        } else {
          dispatch({ type: FILE_VALIDATION_COMPLETE, payload: filesToBeValidated });
        }
      }
      return validationErrors;
    });
  };
}

/**
 * Utils
 */

export function toggleColoringMode() {
  return {
    type: TOGGLE_COLORING_MODE,
  };
}
